/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useRef,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import {ColDef} from 'ag-grid-community';
import {translate} from '@ion/core/src/locale/locale';
import {CTCEntity} from '@ion/core/src/types/entities';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import {
  IonGrid, IonGridAPI, RowModelTypes
} from '@ion/dls/src/grid/IonGrid';
import {
  GridResult
} from '@ion/dls/src/grid/PaginationControls';
import {
  PAGE_SIZES
} from '@ion/dls/src/grid/settings';
import {
  useColumnChooser
} from '@ion/dls/src/grid/useColumnChooser';
import {
  useColumnState
} from '@ion/dls/src/grid/useColumnState';
import {NavigationIcon} from '@ion/dls/src/icons/NavigationIcon';
import {DLSTheme} from '@ion/dls/src/types/types';

export interface ViewDiffDetailsProps {
  rowData?: CTCEntity[];
  setdiffDetails: boolean;
  date?: string;
  onNavigation?: (event: MouseEvent<HTMLButtonElement>, direction: string) => void;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  headerStyle : {
    margin         : '10px',
    display        : 'flex',
    justifyContent : 'space-between'
  },
  flexStyle : {
    display   : 'flex',
    cursor    : 'pointer',
    marginTop : '20px'
  },
  lesserThan : {
    marginRight : '20px'
  },
  actions : {
    display   : 'flex',
    marginTop : '0px'
  },
  directions : {
    marginLeft : '600px'
  }
}));

/**
 * HealthEventsDiffDetails component that displays the Diff Details
 * data for the selected Failsafe Audit log.
 */
export const HealthEventsDiffDetails = ({
  setdiffDetails,
  rowData,
  date,
  onNavigation
}: ViewDiffDetailsProps) => {
  const classes                               = useStyles();
  const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
  const [display, setDisplay]                 = useState<boolean>(false);
  const ionGridRef                            = useRef<IonGridAPI>();

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : getColumnDefinitions(),
    columnState       : null
  });

  const {
    showColumnChooser,
    columnDefinitions
  } = useColumnChooser(definitions);

  const displayHealthEventsGrid = useCallback(() => {
    setDisplay(true);
    setdiffDetails(true);
  }, []);

  const totalRows = Object.keys(rowData).length;

  return (
    <>
      <div className={classes.headerStyle}>
        <div onClick={displayHealthEventsGrid} className={classes.flexStyle}>
          <span className={classes.lesserThan}>
            {translate('admin-devices:grid.columns.lt')}
          </span>
          <span>
            {translate('admin-devices:grid.columns.failsafeEvents')}/{date?.split(',')[0]}
          </span>
        </div>
        <div className={classes.actions}>
          <span className={classes.directions}>
            <IconButton onClick={function(e) {
              onNavigation(e, 'back');
            }}
            icon={<NavigationIcon direction='back' />} />
            <IconButton onClick={function(e) {
              onNavigation(e, 'forward');
            }}
            icon={<NavigationIcon direction='forward' />} />
          </span>
        </div>
      </div>
      <IonGrid omitHeader
        ref={ionGridRef}
        rowModelType={RowModelTypes.ClientSide}
        rowData={rowData}
        totalRows={totalRows}
        augmentItems={constructDiffDetailsGridRows}
        columnDefinitions={columnDefinitions}
        defaultDefinitions={defaultDefinitions}
        showColumnChooser={showColumnChooser}
        refreshRequired={refreshRequired}
        setRefreshRequired={setRefreshRequired}
        checkboxesEnabled={false}
        showHeaderCheckbox={false}
        viewSubGroups={false}
        pageSizes={PAGE_SIZES}
        rowMenusEnabled={false}
        rowHeight={40}
        paginationEnabled
        showCustomPaginationBar
        pageOffset />
    </>
  );
};

/**
 * Takes a list of Health Events failsafe diff counts and constructs a list that
 * can be easily consumed by the IonGrid API
 */
export const constructDiffDetailsGridRows = (rowData): GridResult[] => {
  return Object.keys(rowData).map(item => {
    const {oldValue, newValue} = rowData[item];
    return {fieldName: item, oldValue, newValue};
  }) as GridResult[];
};

/**
 * Returns column definitions for Health Events Diff details grid
 */
const getDefinitions = (): ColDef[] => {
  return [{
    headerName : translate('common:editors.audit.grid.columns.field'),
    field      : 'fieldName',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('common:editors.audit.grid.columns.oldValue'),
    field      : 'oldValue',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('common:editors.audit.grid.columns.newValue'),
    field      : 'newValue',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  }];
};

const getColumnDefinitions = () => {
  const definitions = getDefinitions();
  return {
    desktop : definitions,
    mobile  : definitions
  };
};
export default HealthEventsDiffDetails;
